import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { formatUSPhoneInput, toE164US } from '../utils/phone';
import './MFAEnrollmentPage.css';

const MFAEnrollmentPage = () => {
  const navigate = useNavigate();

  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const firebaseToken = localStorage.getItem('firebaseToken');
  const authHeaders = { Authorization: `Bearer ${firebaseToken}` };

  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState(formatUSPhoneInput(storedUser.phone || ''));
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSkip = () => navigate('/dashboard', { replace: true });

  const handleSendCode = async () => {
    if (!phone.trim()) {
      setError('Please enter your phone number.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await apiClient.post(
        API_ENDPOINTS.verifyPhoneSend,
        { phone: toE164US(phone) || phone.trim() },
        { headers: authHeaders }
      );
      setStep('otp');
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'Failed to send verification code. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setError('Please enter the verification code.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await apiClient.post(
        API_ENDPOINTS.verifyPhoneConfirm,
        { code: otp.trim() },
        { headers: authHeaders }
      );
      await apiClient.post(API_ENDPOINTS.mfaEnable, {}, { headers: authHeaders });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'Invalid verification code. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mfa-page">
      <div className="mfa-card">
        <div className="mfa-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="5" y="11" width="14" height="10" rx="2" ry="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            <circle cx="12" cy="16" r="1" fill="currentColor" />
          </svg>
        </div>

        <h1 className="mfa-title">Enable Two-Factor Authentication</h1>
        <p className="mfa-subtitle">
          Add an extra layer of security to your account with SMS verification.
        </p>

        {step === 'phone' ? (
          <>
            <div className="mfa-input-group">
              <label className="mfa-label">Phone Number</label>
              <input
                type="tel"
                className="mfa-input"
                placeholder="+1 (555) 000-0000"
                value={phone}
                onChange={(e) => { setPhone(formatUSPhoneInput(e.target.value)); setError(''); }}
              />
            </div>

            {error && <p className="mfa-error">{error}</p>}

            <button
              className="mfa-btn-primary"
              onClick={handleSendCode}
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Verification Code'}
            </button>
          </>
        ) : (
          <>
            <p className="mfa-sent-note">
              A 6-digit code was sent to <strong>{phone}</strong>.
            </p>

            <div className="mfa-input-group">
              <label className="mfa-label">Verification Code</label>
              <input
                type="text"
                className="mfa-input mfa-input-otp"
                placeholder="000000"
                value={otp}
                onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            </div>

            {error && <p className="mfa-error">{error}</p>}

            <button
              className="mfa-btn-primary"
              onClick={handleVerifyOtp}
              disabled={isLoading || otp.length < 6}
            >
              {isLoading ? 'Verifying...' : 'Verify & Enable MFA'}
            </button>

            <button
              className="mfa-btn-text"
              onClick={() => { setStep('phone'); setOtp(''); setError(''); }}
            >
              Change phone number
            </button>
          </>
        )}

        <button className="mfa-btn-skip" onClick={handleSkip}>
          Skip for now
        </button>
      </div>
    </div>
  );
};

export default MFAEnrollmentPage;
